import { KYCUserIdentityFormContentRendererProps } from './props';
import UserIdentityFormContentPersonalIdentityLookUp from '../PersonalIdentityLookUp';
import KYCUserIdentityFormContentPhoneNumberLookUp from '../PhoneNumberLookUp';
import {
  useKycInformationHandler,
  useKycNavigation,
} from '@starsoft/common/hooks';
import { KycCheckpointId } from '@starsoft/common/enums';
import { useCoallescedCoinId } from '../../../../../../hooks/coin/useCoallescedCoinId';
import KYCUserIdentityFormContentDocumentSubmission from '../DocumentSubmission';
import UseEmailForm from '@starsoft/common/components/UserEmail/Form';

/**
 * KYC User Identity Form Content Renderer Component
 * Renders different form components based on the KYC checkpoint/step.
 * The component handles the flow between different KYC verification steps like phone number,
 * document submission, email verification, tax ID verification and identity verification.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isContainerColor - Whether to use container background color styling
 * @param {string} props.externalOriginId - External origin ID to override default origin
 * @returns {JSX.Element} The appropriate form component based on the current KYC checkpoint
 *
 * @example
 * // Basic usage
 * <KYCUserIdentityFormContentRenderer
 *   isContainerColor={true}
 *   externalOriginId="US"
 * />
 *
 * // Without container color and external origin
 * <KYCUserIdentityFormContentRenderer
 *   isContainerColor={false}
 * />
 */
export default function KYCUserIdentityFormContentRenderer({
  isContainerColor,
  externalOriginId,
}: KYCUserIdentityFormContentRendererProps) {
  const { coinId } = useCoallescedCoinId();
  const { kycCheckpointId, isValidKycCheckpointId } = useKycNavigation({
    coinId: coinId?.toString(),
  });
  const {
    useKycMeByOriginIdQuery: { kyc },
  } = useKycInformationHandler();

  switch (!isValidKycCheckpointId ? null : kycCheckpointId?.toString()) {
    case KycCheckpointId.VerifiedPhoneNumber?.toString():
      return <KYCUserIdentityFormContentPhoneNumberLookUp isContainerColor />;
    case KycCheckpointId.DocumentSubmission?.toString():
      return (
        <KYCUserIdentityFormContentDocumentSubmission
          originId={externalOriginId}
        />
      );
    case KycCheckpointId.VerifiedEmail?.toString():
      return (
        <UseEmailForm isContainerColor={isContainerColor} useMinimalForm />
      );
    case KycCheckpointId.VerifiedTaxId?.toString():
    case KycCheckpointId.Identity?.toString():
    default:
      return (
        <UserIdentityFormContentPersonalIdentityLookUp
          isContainerColor={isContainerColor}
          readonly={!!kyc?.identity}
          externalOriginId={externalOriginId}
        />
      );
  }
}
